import { useState, useEffect, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
    fetchGarantiPOSSettings,
    upsertGarantiPOSSettings,
    deleteGarantiPOSSettings
} from "../../../api/requests/company-requests";

export const useGarantiPaymentSettings = (companyId) => {
    const [loading, setLoading] = useState(false);
    const [hasSettings, setHasSettings] = useState(false);
    const [settingsId, setSettingsId] = useState(null);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            companyId: companyId || "",
            merchantId: "",
            terminalId: "",
            provUserId: "PROVAUT",
            provisionPassword: "",
            storeKey: "",
            testMode: false
        },
        validationSchema: Yup.object({
            companyId: Yup.string().required("Gerekli"),
            merchantId: Yup.string().required("Merchant ID gerekli"),
            terminalId: Yup.string().required("Terminal ID gerekli"),
            provUserId: Yup.string().required("Provizyon kullanıcısı gerekli"),
            provisionPassword: Yup.string().required("Provizyon şifresi gerekli"),
            storeKey: Yup.string().required("Store Key gerekli")
        }),
        onSubmit: async (values) => {
            setLoading(true);
            try {
                const payload = {
                    companyId: values.companyId,
                    merchantId: values.merchantId,
                    terminalId: values.terminalId,
                    provUserId: values.provUserId,
                    provisionPassword: values.provisionPassword,
                    storeKey: values.storeKey,
                    testMode: values.testMode
                };

                const res = await upsertGarantiPOSSettings(payload);
                if (res?.success || res?.data) {
                    setHasSettings(true);
                    if (res?.data?.id) setSettingsId(res.data.id);
                    toast.success("Garanti BBVA ayarları güncellendi");
                } else {
                    toast.error(res?.message || "Garanti BBVA ayarları kaydedilemedi");
                }
            } catch (error) {
                toast.error("Garanti BBVA ayarları kaydedilirken hata oluştu");
            } finally {
                setLoading(false);
            }
        }
    });

    useEffect(() => {
        if (!companyId) return;

        const loadSettings = async () => {
            try {
                const res = await fetchGarantiPOSSettings(companyId);

                if (res?.data) {
                    setHasSettings(true);
                    setSettingsId(res.data.id ?? null);
                    // Güvenlik: provisionPassword ve storeKey backend'den DÖNMEZ; boş bırakılır,
                    // güncelleme için tekrar girilmeleri gerekir.
                    formik.setValues({
                        companyId,
                        merchantId: res.data.merchantId ?? "",
                        terminalId: res.data.terminalId ?? "",
                        provUserId: res.data.provUserId ?? "PROVAUT",
                        provisionPassword: "",
                        storeKey: "",
                        testMode: res.data.testMode ?? false
                    });
                } else {
                    // Kayıt yoksa backend statusCode:400 döner → hata değil, boş form durumu.
                    setHasSettings(false);
                    setSettingsId(null);
                }
            } catch (error) {
                console.error("Garanti BBVA ayarları yüklenemedi", error);
                setHasSettings(false);
                setSettingsId(null);
            }
        };

        loadSettings();
    }, [companyId]);

    // Bağlantıyı kaldır: backend'deki kaydı siler ama girilen form bilgilerini
    // olduğu gibi bırakır (tekrar bağlanmak isterse yeniden yazmasın diye).
    const disconnect = useCallback(async () => {
        // Henüz kaydedilmemiş (yerel) bağlantı → sadece bağlı durumunu düşür.
        if (!settingsId) {
            setHasSettings(false);
            return true;
        }
        try {
            const res = await deleteGarantiPOSSettings(settingsId);
            if (res?.statusCode === 200 || res?.data === true) {
                setHasSettings(false);
                setSettingsId(null);
                return true;
            }
            return false;
        } catch (error) {
            console.error("Garanti BBVA bağlantısı kaldırılamadı", error);
            return false;
        }
    }, [settingsId]);

    return { formik, loading, hasSettings, disconnect };
};
